import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getFamiliaById, createFamilia, updateFamilia, deleteFamilia } from "../../services/familiasService";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Separator } from "../../components/ui/separator";
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { useContextualNavigation } from '../../hooks/useContextualNavigation';

export default function FamiliasForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id);
  
  const { getListRoute } = useContextualNavigation();
  const familiasListRoute = getListRoute('familias');
  
  const [formData, setFormData] = useState({
    titulo: "",
    descripcion: "",
    urlImagen: "",
    colorDestacado: "#0693e3",
    status: "active",
    archived: false
  });
  
  const [imagePreview, setImagePreview] = useState("");
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Load data if editing
  useEffect(() => {
    if (isEditing) {
      const loadFamilia = async () => {
        try {
          setLoading(true);
          const data = await getFamiliaById(id);
          setFormData({
            titulo: data.titulo || "", // Changed from data.nombre to data.titulo
            descripcion: data.descripcion || "",
            urlImagen: data.urlImagen || "",
            colorDestacado: data.colorDestacado || "#0693e3",
            status: data.status || "active",
            archived: data.archived === 1
          });
          setImagePreview(data.urlImagen || "");
        } catch (err) {
          console.error("Error loading familia:", err);
          setError("No se pudo cargar la información de la familia");
        } finally {
          setLoading(false);
        }
      };
      loadFamilia();
    } else {
      setImagePreview(formData.urlImagen);
    }
  }, [id, isEditing]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? (checked ? 'active' : 'inactive') : value;
    
    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));
    
    if (name === 'urlImagen') {
      setImagePreview(value);
    }
  };

  // Handle archive toggle
  const handleArchiveToggle = async (newArchivedState) => {
    try {
      setLoading(true);
      // Convert boolean to number (1 or 0) for FileMaker
      await updateFamilia(id, {
        archived: newArchivedState ? 1 : 0
      });
      
      setFormData(prev => ({
        ...prev,
        archived: newArchivedState
      }));
      
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error('Error al actualizar estado de archivo:', err);
      setError('Error al actualizar estado de archivo. Por favor, intente nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    try {
      setLoading(true);
      await deleteFamilia(id);
      navigate(familiasListRoute); // En lugar de '/familias'
    } catch (err) {
      console.error("Error deleting familia:", err);
      setError("No se pudo eliminar la familia");
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      if (isEditing) {
        // For editing, only send changed fields
        const originalData = await getFamiliaById(id);
        const changedData = {};
        
        if (formData.titulo !== originalData.titulo) changedData.titulo = formData.titulo; // Changed from originalData.nombre
        if (formData.descripcion !== originalData.descripcion) changedData.descripcion = formData.descripcion;
        if (formData.urlImagen !== originalData.urlImagen) changedData.urlImagen = formData.urlImagen;
        if (formData.colorDestacado !== originalData.colorDestacado) changedData.colorDestacado = formData.colorDestacado;
        if (formData.status !== originalData.status) changedData.status = formData.status;
        
        await updateFamilia(id, changedData);
      } else {
        // For creating, send all data
        await createFamilia(formData);
      }
      navigate("/admin/familias");
    } catch (err) {
      console.error(`Error ${isEditing ? 'updating' : 'creating'} familia:`, err);
      setError(`No se pudo ${isEditing ? 'actualizar' : 'crear'} la familia`);
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6"> {/* Added p-6 padding */}
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">{id ? 'Editar' : 'Nueva'} Familia</h1>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert>
          <AlertDescription>
            Familia actualizada correctamente
          </AlertDescription>
        </Alert>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="titulo">Nombre</Label>
              <Input
                id="titulo"
                name="titulo"
                value={formData.titulo}
                onChange={handleChange}
                placeholder="Nombre de la familia"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="descripcion">Descripción</Label>
              <Textarea
                id="descripcion"
                name="descripcion"
                value={formData.descripcion}
                onChange={handleChange}
                placeholder="Descripción de la familia"
                rows={4}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="urlImagen">URL de la Imagen</Label>
              <Input
                id="urlImagen"
                name="urlImagen"
                value={formData.urlImagen}
                onChange={handleChange}
                placeholder="https://yooldigital.com/assets/default.png"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="colorDestacado">Color Destacado</Label>
              <div className="flex items-center space-x-3">
                <div 
                  className="w-10 h-10 rounded-full border border-gray-300" 
                  style={{ backgroundColor: formData.colorDestacado }}
                />
                <Input
                  id="colorDestacado"
                  name="colorDestacado"
                  type="color"
                  value={formData.colorDestacado}
                  onChange={handleChange}
                  className="w-20 h-10 p-1"
                />
                <Input 
                  type="text" 
                  name="colorDestacado"
                  value={formData.colorDestacado}
                  onChange={handleChange}
                  className="flex-1"
                  placeholder="#000000"
                />
              </div>
            </div>

            {!formData.archived && (
              <div className="flex items-center space-x-2 pt-4">
                <Switch
                  id="status"
                  checked={formData.status === 'active'}
                  onCheckedChange={(checked) => {
                    setFormData(prev => ({
                      ...prev,
                      status: checked ? 'active' : 'inactive'
                    }));
                  }}
                />
                <Label htmlFor="status" className="cursor-pointer">
                  {formData.status === 'active' ? "Activo" : "Inactivo"}
                </Label>
              </div>
            )}
          </div>

          <div>
            <Card className="overflow-hidden">
              <div className="relative h-64 overflow-hidden">
                <img 
                    src={imagePreview || "https://yooldigital.com/assets/default.png"} 
                    alt="Vista previa" 
                    className="w-full h-48 object-cover object-top rounded-md"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "https://yooldigital.com/assets/default.png";
                    }}
                  />
                <div 
                  className="absolute top-0 left-0 w-full h-2" 
                  style={{ backgroundColor: formData.colorDestacado }}
                />
              </div>
              <CardContent className="p-4">
                <h3 className="text-lg font-semibold">Vista Previa</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Así se verá la tarjeta de la familia en el listado
                </p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Archive toggle section (only for edit mode) */}
        {isEditing && (
          <div className="border-t pt-6 mt-6">
            <h2 className="text-xl font-semibold mb-4">Estado de Archivo</h2>
            <div className="flex items-center space-x-4">
              <Switch 
                checked={formData.archived} 
                onCheckedChange={handleArchiveToggle}
                disabled={loading}
              />
              <div>
                <p className="font-medium">{formData.archived ? 'Archivado' : 'No Archivado'}</p>
                <p className="text-sm text-gray-500">
                  {formData.archived 
                    ? 'Esta familia está archivada y no aparecerá en las listas principales.' 
                    : 'Esta familia está activa y visible en las listas principales.'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Danger Zone (only for edit mode) */}
        {isEditing && (
          <div className="border-t border-red-200 pt-6 mt-6">
            <h2 className="text-xl font-semibold text-[var(--yool-error)] mb-4">Zona de Peligro</h2>
            <p className="text-sm text-gray-500 mb-4">
              Las siguientes acciones son irreversibles. Por favor, tenga cuidado.
            </p>
            <Button
              type="button"
              variant="outline"
              className="border-red-500 text-red-500 hover:bg-red-50"
              onClick={() => setDeleteDialogOpen(true)}
            >
              Eliminar Familia Permanentemente
            </Button>
          </div>
        )}

        {/* REMOVE THE DUPLICATE SECTIONS BELOW */}
        
        <div className="flex justify-end space-x-4 pt-4">
          <Button type="button" variant="outline" onClick={() => navigate(familiasListRoute)}>
            Cancelar
          </Button>
          <Button type="submit" disabled={saving}>
            {saving ? "Guardando..." : isEditing ? "Actualizar" : "Crear"}
          </Button>
        </div>
      </form>

      {/* Delete confirmation dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-white rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Estás seguro?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Se eliminará permanentemente la familia 
              "{formData.titulo}".
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="text-[#8E8E93] border-[#8E8E93] hover:bg-gray-50">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete} 
              className="bg-[#FF3B30] hover:bg-[#D70015] text-white"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}